import type { ReactNode } from "react";

export interface IColumn<T> {
  header: string;
  accessor: keyof T;
  render?: (row: T) => ReactNode;
}

interface IPagination {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}

interface TableProps<T> {
  title?: string;
  headles?: boolean;
  data: T[];
  columns: IColumn<T>[];
  pagination?: IPagination;
  children?: ReactNode;
}

export default function Table<T extends object>({
  title,
  headles,
  data,
  columns,
  pagination,
  children,
}: Readonly<TableProps<T>>) {
  const totalPages = pagination
    ? Math.ceil(pagination.total / pagination.pageSize)
    : 1;

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-4">
        {title && (
          <h2 className="text-lg font-semibold whitespace-nowrap overflow-hidden text-ellipsis flex-1 mr-5 text-white">
            {title}
          </h2>
        )}
        <div className="flex gap-4">{children}</div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          {!headles && (
            <thead>
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.accessor as string}
                    className="px-4 py-2 text-left font-medium text-xs uppercase tracking-wide text-white/60">
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
          )}

          <tbody>
            {data.length > 0 ? (
              data.map((row, rowIndex) => (
                <tr
                  key={"rowIndex-" + rowIndex}
                  className="transition-colors hover:bg-white/5">
                  {columns.map((col, colIndex) => (
                    <td
                      key={(col.accessor as string) + colIndex}
                      className="px-4 py-3 text-sm text-white/80 border-b border-white/10">
                      {col.render ? (
                        col.render(row)
                      ) : (
                        <span className="whitespace-nowrap">
                          {String(row[col.accessor])}
                        </span>
                      )}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-center py-6 text-white/40">
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && totalPages > 1 && (
        <div className="flex justify-between items-center mt-4">
          <span className="text-sm text-white/60">
            Page {pagination.page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              className="px-3 py-1 border border-white/20 text-white/70 rounded disabled:opacity-50 disabled:text-white/40 disabled:border-white/10"
              onClick={() => pagination.onPageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}>
              Prev
            </button>
            <button
              className="px-3 py-1 border border-white/20 text-white/70 rounded disabled:opacity-50 disabled:text-white/40 disabled:border-white/10"
              onClick={() => pagination.onPageChange(pagination.page + 1)}
              disabled={pagination.page >= totalPages}>
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
